import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as Swetrix from "swetrix";
import { api } from "@/lib/api";
import { getConsent, setAnalyticsEnabled, setConsentRequired, CONSENT_EVENT } from "@/lib/consent";

const IGNORE = [/^\/admin/, /^\/login/];
const isIgnored = (path) => IGNORE.some((re) => re.test(path));

export default function Analytics() {
  const location = useLocation();
  const cfg = useRef(null);
  const gaReady = useRef(false);
  const inited = useRef(false);
  const lastPath = useRef("");

  const sendGa = (id) => {
    if (!window.gtag) return;
    const path = window.location.pathname + window.location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
      send_to: id,
    });
  };

  // Load Swetrix + GA only once, and only when consent allows it.
  const doInit = () => {
    if (inited.current || !cfg.current?.enabled) return;
    inited.current = true;
    setAnalyticsEnabled(true);

    if (cfg.current.swetrix_pid) {
      try {
        Swetrix.init(cfg.current.swetrix_pid);
        Swetrix.trackViews({ ignore: IGNORE, heartbeatOnBackground: true });
      } catch (e) { console.debug("analytics: Swetrix init failed", e); }
    }

    if (cfg.current.ga_id) {
      const id = cfg.current.ga_id;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", id, { send_page_view: false });
      gaReady.current = true;
      if (!isIgnored(window.location.pathname)) sendGa(id);
    }
  };

  useEffect(() => {
    let mounted = true;
    let onConsent = null;
    Promise.all([
      api.get("/public/analytics").then((r) => r.data).catch(() => ({})),
      api.get("/public/popups").then((r) => r.data?.cookie).catch(() => null),
    ]).then(([acfg, cookie]) => {
      if (!mounted) return;
      cfg.current = acfg || {};
      const consentRequired = !!cookie?.enabled;
      setConsentRequired(consentRequired);
      if (!cfg.current.enabled) return;

      if (!consentRequired || getConsent() === "accepted") {
        doInit();
        return;
      }
      // Consent required but not yet accepted — wait for the visitor's choice.
      onConsent = () => {
        if (getConsent() === "accepted") {
          doInit();
          if (onConsent) window.removeEventListener(CONSENT_EVENT, onConsent);
        }
      };
      window.addEventListener(CONSENT_EVENT, onConsent);
    });
    return () => { mounted = false; if (onConsent) window.removeEventListener(CONSENT_EVENT, onConsent); };
  }, []);

  useEffect(() => {
    const id = cfg.current?.ga_id;
    if (!gaReady.current || !id) return;
    if (isIgnored(location.pathname)) return;
    sendGa(id);
  }, [location.pathname, location.search]);

  return null;
}
